/**
 * Temporary screen-scoped design tokens for **Settings → Support Tickets**.
 *
 * Mirrors the Settings design (`Settings.dc.html`, "Support Tickets" section)
 * 1:1 — every colour, size, radius, shadow, spacing and type step the tickets
 * list needs is captured here so the components never carry a raw literal.
 * These stay screen-local on purpose: once every settings screen has been
 * re-skinned the shared values get promoted into the MUI theme and this file
 * is deleted.
 */

import { contentCard, inkfree, notification, signer, switchControl, white } from "theme/colors";

export const supportTicketsTokens = {
  color: {
    /** Deep brand ink — ticket summaries, empty-state heading. */
    ink: signer.ink,
    /** Secondary text — inactive tabs, category, raised-by name. */
    muted: signer.text.meta,
    /**
     * Column-header strip text, empty-state sentence, timestamps.
     *
     * WCAG 2.1 AA 1.4.3: the design's `#9B96AE` renders at 2.85:1 on the white
     * card and 2.83:1 on the `headerBg` strip — well under the 4.5:1 required
     * for this (sub-18.66px) type. Darkened along the same lavender hue to the
     * lightest value that clears 4.5:1 against both backgrounds (4.85:1 on
     * white, 4.63:1 on `headerBg`).
     */
    subtle: "#736E8A",
    surface: white,
    /** Card chrome. */
    cardBorder: contentCard.borderMuted,
    /** Tab strip rule + column-header strip fill/rule + footer top rule. */
    tabBarBorder: signer.border.light,
    headerBg: signer.surface.subtle,
    headerBorder: signer.border.light,
    /** Rule between ticket rows. */
    rowDivider: signer.border.divider,
    /** Active tab text/underline — a thin accent, stays orange. */
    accent: signer.orange.main,
    /**
     * Primary action ("New Ticket") + active page item — solid fills, so
     * per the standing brand-colour rule these are the app brand red, not
     * `accent` above.
     */
    ctaBg: inkfree.main,
    ctaBgHover: inkfree.dark,
    onAccent: white,
    /** Round icon well behind the empty-state glyph. */
    emptyIconBg: signer.surface.muted,
    /** "Unassigned" pill. */
    pillText: signer.text.meta,
    pillBg: signer.surface.muted,
    /**
     * Priority dots + labels.
     *
     * WCAG 2.1 AA 1.4.3: the design's `#D64545` / `#E1500F` / `#1E9E5A` render
     * as 12.5px label text at 4.39:1, 3.92:1 and 3.45:1 on white. Each is
     * darkened along its own hue to the lightest AA-passing value (4.86:1,
     * 4.93:1, 4.97:1); the dot shares the label colour so the pair stays
     * visually matched.
     */
    priorityHigh: "#CB3F3F",
    priorityMedium: signer.orange.dark,
    priorityLow: "#178049",
    /** Inactive pagination item. */
    pageItem: signer.text.meta,
    pageHoverFill: signer.surface.muted,
    /** Disabled pagination arrow. */
    pageDisabled: switchControl.trackOff,
  },

  font: {
    tab: "13px",
    tabCount: "11px",
    columnHeader: "10.5px",
    summary: "13px",
    cell: "12.5px",
    timestamp: "11px",
    pill: "11.5px",
    emptyTitle: "13px",
    emptyText: "12px",
    button: "13px",
    pageItem: "12px",
  },

  weight: {
    strong: 700,
    medium: 600,
    regular: 500,
  },

  letterSpacing: {
    columnHeader: "0.09em",
  },

  radius: {
    card: "14px",
    control: "9px",
    pageItem: "7px",
    pill: "999px",
  },

  border: {
    hairline: "1px",
    /** Underline under the active tab. */
    tabIndicator: "2px",
  },

  shadow: {
    card: "0 1px 3px rgba(33, 21, 71, 0.04)",
  },

  space: {
    /** Horizontal padding shared by the tab strip, header strip, rows, footer. */
    gutter: "20px",
    tabBarPaddingTop: "6px",
    tabGap: "4px",
    tabPaddingY: "12px",
    tabPaddingX: "14px",
    tabCountGap: "6px",
    columnHeaderY: "10px",
    rowY: "15px",
    footerY: "14px",
    /** Gap between the priority dot and its label. */
    priorityGap: "8px",
    pillPaddingY: "3px",
    pillPaddingX: "11px",
    /** Gap between the raised-by name and its timestamp. */
    stackedCellGap: "2px",
    paginationGap: "4px",
    emptyPadding: "48px 28px",
    emptyGap: "8px",
  },

  size: {
    buttonHeight: "40px",
    buttonPaddingX: "20px",
    priorityDot: "6px",
    emptyIconWell: "48px",
    emptyIcon: "20px",
    emptyIconStrokeWidth: 2,
    pageItem: "28px",
    pageArrow: "11px",
    pageArrowStrokeWidth: 2.5,
  },

  /** Grid template copied verbatim from the design. */
  grid: {
    row: "56px 1fr 150px 110px 150px 150px",
  },
} as const;

/**
 * Phone (`xs`) and tablet (`sm`) values for **Settings → Support Tickets**,
 * traced from `Settings - Mobile & Tablet.dc.html`.
 *
 * The reference draws both sizes identically — only the gutters around them
 * change, and `SettingsLayout` already owns those — so unlike the other screens
 * there is a single set of values here rather than a phone/tablet split. The
 * desktop table is untouched at `md`.
 */
export const supportTicketsResponsiveTokens = {
  /** Search box and the full-width "Create Ticket" action above the list. */
  action: {
    height: "44px",
    radius: "9px",
    paddingX: "12px",
    buttonPaddingX: "20px",
    fontSize: "13px",
  },
  /** Segmented Open / In Process / Complete control. */
  segmented: {
    trackBg: signer.border.light,
    trackRadius: "11px",
    trackPadding: "4px",
    gap: "4px",
    itemHeight: "38px",
    itemRadius: "8px",
    fontSize: "12px",
    activeBg: white,
    activeColor: signer.ink,
    activeShadow: "0 1px 3px rgba(33,21,71,.14)",
    idleColor: signer.text.subtitle,
  },
  /** One ticket, drawn as its own card instead of a table row. */
  card: {
    border: contentCard.borderMuted,
    radius: "14px",
    padding: "15px 16px",
    gap: "9px",
    headGap: "9px",
    numberFontSize: "12px",
    priorityFontSize: "10px",
    priorityPadding: "3px 9px",
    categoryFontSize: "11px",
    categoryColor: signer.text.faint,
    summaryFontSize: "13.5px",
    summaryLineHeight: 1.4,
    footerPaddingTop: "9px",
    footerBorder: signer.surface.muted,
    footerGap: "8px",
    avatarSize: "24px",
    avatarBg: signer.surface.muted,
    avatarColor: signer.ink,
    avatarFontSize: "9px",
    reporterFontSize: "11.5px",
    dateFontSize: "11px",
  },
  /**
   * Priority chip fills. The label colours reuse the AA-corrected values in
   * `color` above; only the tints are new.
   */
  priorityTint: {
    High: notification.error.bg,
    Medium: "#FDF3D7",
    Low: notification.success.bg,
  } as Record<string, string>,
} as const;

export default supportTicketsTokens;
